import React, { useState, useCallback, useEffect } from "react";
import { addNotification as notify } from "reapop";
import { connect } from "react-redux";
import Select from "../../forms/Select";
import Textarea from "../../forms/Textarea";
import {
  EditTranslationsDraftFields,
  languages,
} from "./ManageTranslationField";
import { validateField } from "../../pages/functions";
import AdminNavigation from "./AdminNavigation";
import { api } from "../index.api";
import "./styles.css";
import { REF_DATA } from "../../constants";
import { getApplicationRegex } from "../plugins/utils";

const EditTranslationsDraft = (props) => {
  const [fields, setFields] = useState(EditTranslationsDraftFields);
  const [counteryFieldOptions, setCountryFieldOptions] = useState([]);
  const [editJsonFieldsOptions, setEditJsonFieldsOptions] = useState([]);

  const [createDraftState, setCreateDraftState] = useState(false);
  // State for selected values
  const [selectedCountry, setSelectedCountry] = useState("");
  const [selectedPage, setSelectedPage] = useState("");
  const [selectedUserType, setSelectedUserType] = useState("");
  const [selectedSection, setSelectedSection] = useState("");
  const [selectedElement, setSelectedElement] = useState("");
  const [selectedJSONKey, setSelectedJSONKey] = useState("");
  const [liveTranslationFields, setLiveTranslationFields] = useState(languages);
  const [draftTranslationFields, setDraftTranslationFields] =
    useState(languages);
  const [liveTranslationValues, setLiveTranslationValues] = useState([]);
  const [draftTranslationValues, setDraftTranslationValues] = useState([]);

  const resetFiledValues = useCallback(() => {
    let fieldsValue = fields;
    fieldsValue.country.value = "";
    setFields({ ...fieldsValue });
    setSelectedCountry("");
    setSelectedPage("");
    setSelectedUserType("");
    setSelectedSection("");
    setSelectedElement("");
    setSelectedJSONKey("");
  }, []);

  const getLiveAndDraftDetails = useCallback(() => {
    api.admin.translation.fetchLiveAndDraftDetails((response) => {
      const draftVersionDetails = response.filter(
        (res) => res.type.toLowerCase() === "draft"
      );
      if (draftVersionDetails.length > 0) {
        setCreateDraftState(true);
      }
    });
  }, [fields]);

  const getDistinctLiveCountryList = useCallback((COUNTRY) => {
    api.admin.translation.getDistinctLiveCountryList((countryList) => {
      if (countryList.length > 0) {
        const countryOptions = countryList.map((countryCode) => ({
          value: countryCode,
          label:
            countryCode.toLowerCase() === "us"
              ? "United States & Others"
              : Array.isArray(COUNTRY)
              ? COUNTRY.find(
                  (countryDetails) =>
                    countryDetails.abbrevation.toLowerCase() ===
                    countryCode.toLowerCase()
                ).name || "Country Not Found"
              : "Invalid COUNTRY Data",
        }));
        setCountryFieldOptions(countryOptions);
      }
    });
  }, []);

  useEffect(() => {
    resetFiledValues();
    api.fetchCountriesList((response) => {
      getLiveAndDraftDetails();
      getDistinctLiveCountryList(response.COUNTRY);
      api.admin.translation.fetchJsonKeys((JsonKeys) => {
        setEditJsonFieldsOptions(JsonKeys);
      });
    });
  }, []);

  const showNotification = (title, message, status) => {
    props.notify({
      title,
      message,
      status,
      position: "tr",
      dismissible: true,
      dismissAfter: 3000,
    });
  };

  // Extract unique pages from the data
  const pages = selectedCountry
    ? [...new Set(editJsonFieldsOptions.map((item) => item.page))].map(
        (page) => ({
          value: page,
          label: page,
        })
      )
    : [];

  // Filter user types based on selected page and return unique user types
  const userTypes =
    selectedCountry && selectedPage
      ? [
          ...new Set(
            editJsonFieldsOptions
              .filter((item) => item.page === selectedPage)
              .map((item) => item.userType)
          ),
        ].map((userType) => ({
          value: userType,
          label: userType,
        }))
      : [];

  // Filter sections based on selected page and user type, return unique sections
  const sections =
    selectedCountry && selectedPage && selectedUserType
      ? [
          ...new Set(
            editJsonFieldsOptions
              .filter(
                (item) =>
                  item.page === selectedPage &&
                  item.userType === selectedUserType
              )
              .map((item) => item.section)
          ),
        ].map((sections) => ({
          value: sections,
          label: sections,
        }))
      : [];

  // Filter elements based on selected page, user type, and section, return unique elements
  const elements =
    selectedCountry && selectedPage && selectedUserType && selectedSection
      ? [
          ...new Set(
            editJsonFieldsOptions
              .filter(
                (item) =>
                  item.page === selectedPage &&
                  item.userType === selectedUserType &&
                  item.section === selectedSection
              )
              .map((item) => item.elementDisplayText)
          ),
        ].map((elements) => ({
          value: elements,
          label: elements,
        }))
      : [];

  const getElementJsonKey = (page, userType, section, elementDisplayText) => {
    const result = editJsonFieldsOptions.find(
      (item) =>
        item.page === page &&
        item.userType === userType &&
        item.section === section &&
        item.elementDisplayText === elementDisplayText
    );

    // If found, return the Element_json_key; otherwise, return null
    return result ? result.elementJSONKey : null;
  };

  const handleElementChange = (e) => {
    setSelectedElement(e);
    const jsonKey = getElementJsonKey(
      selectedPage,
      selectedUserType,
      selectedSection,
      e
    );
    setSelectedJSONKey(jsonKey);
    const queryParams = {
      countryCode: selectedCountry,
      elementKey: jsonKey,
    };
    api.admin.translation.fetchLiveJsonElementValue(queryParams, (response) => {
      if (Object.keys(response).length > 0) {
        setLiveTranslationValues(response);
        const updatedLiveTranslationFields = liveTranslationFields.map(
          (field) => ({
            ...field,
            value:
              response[field.languageCode] === null
                ? "Text not available"
                : response[field.languageCode],
          })
        );
        setLiveTranslationFields(updatedLiveTranslationFields);
      }
    });
    api.admin.translation.fetchDraftJsonElementValue(queryParams, (response) => {
      if (Object.keys(response).length > 0) {
        setDraftTranslationValues(response);
        const updatedDraftTranslationFields = draftTranslationFields.map(
          (field) => ({
            ...field,
            value:
              response[field.languageCode] === null
                ? "Text not available"
                : response[field.languageCode],
          })
        );
        setDraftTranslationFields(updatedDraftTranslationFields);
      }
    });
  };

  const regexValidation = useCallback((inputValue)=>{
    const ref_Data = props.referenceData.find(function (category) {
      return category.type === REF_DATA;
    }).referenceData;
    const regex = getApplicationRegex(ref_Data);

    if (regex.Emoji_Regex !== null && regex.Emoji_Regex.test(inputValue)) {
      // Emoji detected, prevent it from being added
      showNotification(
        "Error!",
        "Special characters are not allowed",
        "error"
      );
      return;
    } else if (regex.Script_Regex !== null && regex.Script_Regex.test(inputValue)) {
      // HTML Tags and Script detected, prevent it from being added
      showNotification(
        "Error!",
        "Submission fails. Input contains prohibited words. Please remove them and try again.",
        "error"
      );
      return;
    } else if (regex.Script_Regex === null || regex.Emoji_Regex === null) {
      showNotification(
        "Error!",
        "Validation configuration is missing. Please contact support.",
        "error"
      );
      return;
    }
    return true;
  });

  const validate = useCallback((e, field) => {
    const inputValue = e.target.value.toLowerCase();
    const validated = regexValidation(inputValue);
    if(validated){
      const validateFields = validateField(e, field, fields);
      setFields({ ...validateFields });
    }
  }, []);

  const validateDradtTranslations = useCallback(
    (e, field) => {
      const validated = regexValidation(e.target.value);
      if(validated){
      const draftValidateFields = draftTranslationFields.map((fieldDetails) =>
        fieldDetails.languageCode === field.languageCode
          ? { ...fieldDetails, value: e.target.value } // Update the value if languageCode matches
          : fieldDetails
      ); // Otherwise, keep the original field
      setDraftTranslationFields(draftValidateFields);
    }
    },
    [draftTranslationFields]
  );

  const handleCountry = (e) => {
    setSelectedCountry(e);
    setSelectedElement("");
    let fieldsValue = fields;
    fieldsValue.page.value = "";
    fieldsValue.userType.value = "";
    fieldsValue.section.value = "";
    fieldsValue.element.value = "";
    setFields({ ...fieldsValue });
  };

  const handlePage = (e) => {
    setSelectedPage(e);
    setSelectedElement("");
    let fieldsValue = fields;
    fieldsValue.userType.value = "";
    fieldsValue.section.value = "";
    fieldsValue.element.value = "";
    setFields({ ...fieldsValue });
  };
  const handleUserType = (e) => {
    setSelectedUserType(e);
    setSelectedElement("");
    let fieldsValue = fields;
    fieldsValue.section.value = "";
    fieldsValue.element.value = "";
    setFields({ ...fieldsValue });
  };
  const handleSection = (e) => {
    setSelectedSection(e);
    setSelectedElement("");
    let fieldsValue = fields;
    fieldsValue.element.value = "";
    setFields({ ...fieldsValue });
  };

  const updateDraftLiveHandler = (res) => {
    if (res.status === 200) {
      setSelectedElement("");
      let fieldsValue = fields;
      fieldsValue.country.value = "";
      fieldsValue.page.value = "";
      fieldsValue.userType.value = "";
      fieldsValue.section.value = "";
      fieldsValue.element.value = "";
      setFields({ ...fieldsValue });
      showNotification("Success", "Changes Updated successfully", "success");
    } else {
      showNotification("Error", "Please try again later", "error");
    }
  };

  const handleSubmit = useCallback(() => {
    const draftedValues = draftTranslationFields.reduce((result, field) => {
      result[field.languageCode] =
        field.value === "Text not available" ? null : field.value; // Add the languageCode as the key and value as the value
      return result;
    }, {});
    const payload = {
      countryCode: selectedCountry,
      elementKey: selectedJSONKey,
      languageValue: draftedValues,
    };

    api.admin.translation.updateDraftJsonElementValues(payload, updateDraftLiveHandler);
  }, [draftTranslationFields, selectedCountry, selectedJSONKey]);

  return (
    <div id="manage-admin-list" className="v2_container">
      <div className="row">
        <AdminNavigation className="col-xl-2 col-lg-3 col-md-4 col-6" />
        <div className="col-xl-10 col-lg-9 col-md-8 col-6 p-l-15">
          <div className="row manage-admin-list-header">
            <div className="col-12 col-xl-12">
              <h3 className="mb-3 header-primary">Edit Translations Draft</h3>
            </div>
          </div>
          {!createDraftState && (
            <h4>
              There is no draft version in progress, go to Manage Translation
              Draft to create it
            </h4>
          )}
          {createDraftState && (
            <div>
              <div>
                <div className="row  mb-4">
                  <div className=" col-md-4">
                    <Select
                      searchable
                      clearable={false}
                      options={counteryFieldOptions}
                      field={fields.country}
                      validate={(e) => validate(e, fields.country)}
                      onChange={(e) => handleCountry(e)}
                    />
                  </div>
                  <div className=" col-md-4">
                    <Select
                      searchable
                      clearable={false}
                      options={pages}
                      field={fields.page}
                      validate={(e) => validate(e, fields.page)}
                      onChange={(e) => handlePage(e)}
                    />
                  </div>
                  <div className=" col-md-4">
                    <Select
                      searchable
                      clearable={false}
                      options={userTypes}
                      field={fields.userType}
                      validate={(e) => validate(e, fields.userType)}
                      onChange={(e) => handleUserType(e)}
                    />
                  </div>
                </div>
                <div className="row  mb-4">
                  <div className=" col-md-4">
                    <Select
                      searchable
                      clearable={false}
                      options={sections}
                      field={fields.section}
                      validate={(e) => validate(e, fields.section)}
                      onChange={(e) => handleSection(e)}
                    />
                  </div>
                  <div className=" col-md-8">
                    <Select
                      searchable
                      clearable={false}
                      options={elements}
                      field={fields.element}
                      validate={(e) => validate(e, fields.element)}
                      onChange={(e) => handleElementChange(e)}
                    />
                  </div>
                </div>
              </div>
              {selectedElement &&
                Object.keys(liveTranslationValues).length > 0 &&
                Object.keys(draftTranslationValues).length > 0 &&
                languages.map((itemLanguage) => {
                  const liveField = liveTranslationFields.find(
                    (field) => field.languageCode === itemLanguage.languageCode
                  );
                  const draftField = draftTranslationFields.find(
                    (field) => field.languageCode === itemLanguage.languageCode
                  );
                  const detectedChanges = liveField.value !== draftField.value;
                  return (
                    <div className="row">
                      <div className="col-lg-6 editJson_Field_Header_Wrapper">
                        <div className="col-12 editJson_Field_Header_Live">{`${liveField.name} : Live`}</div>
                        <Textarea
                          rows={4}
                          maxLength={4000}
                          style={{ borderRadius: "4px" }}
                          field={liveField}
                          isDisabled={true}
                        />
                      </div>
                      <div className="col-lg-6">
                        <div className="col-12 editJson_Field_Header_Draft">
                          {`${draftField.name} : Draft `}
                          <span className="col-12 editJson_Field_Header_Change">
                            {detectedChanges ? " *Changes detected" : ""}
                          </span>
                        </div>
                        <Textarea
                          rows={4}
                          maxLength={4000}
                          style={{ borderRadius: "4px" }}
                          isDisabled={draftField.value === "Text not available"}
                          field={draftField}
                          validate={(e) =>
                            validateDradtTranslations(e, draftField)
                          }
                        />
                      </div>
                    </div>
                  );
                })}
              {selectedElement && (
                <div>
                  <button
                    className="btn btn-lg btn-info btn-rounded save_button"
                    onClick={handleSubmit}
                  >
                    Save
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    referenceData: state.referenceData
  };
}

export default connect(mapStateToProps, { notify })(EditTranslationsDraft);



// WEBPACK FOOTER //
// ./src/mi-app/adminPanel/EditTranslationsDraft.js